"use client";

import { useEffect, useRef } from "react";

/**
 * Promosyonlar görünür olduğunda view_promotion ecommerce event'i tetikler.
 * Intersection Observer ile viewport'a girdiğinde bir kez tetiklenir.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Sarılacak içerik
 * @param {Array} props.items - Kampanya/promosyon item'ları
 * @param {string} [props.creativeSlotPrefix="Body"] - creative_slot öneki (Body 1, Body 2...)
 * @param {Function} [props.getItemPayload] - (item, index) => { creative_name, creative_slot, promotion_id, promotion_name }
 */
export default function ViewPromotionTracker({
  children,
  items = [],
  creativeSlotPrefix = "Body",
  getItemPayload,
}) {
  const containerRef = useRef(null);
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (!items?.length || hasFiredRef.current) return;

    const defaultGetItemPayload = (item, index) => ({
      creative_name: item.TitleWithoutHtmlTags || item.Title || item.title || "",
      creative_slot: `${creativeSlotPrefix} ${index + 1}`,
      promotion_id: String(item.CampaignId ?? item.campaignId ?? ""),
      promotion_name: item.TitleWithoutHtmlTags || item.Title || item.title || "",
    });

    const buildEcommerceItems = () =>
      items.map((item, index) =>
        getItemPayload
          ? getItemPayload(item, index)
          : defaultGetItemPayload(item, index)
      );

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting || hasFiredRef.current) return;

        hasFiredRef.current = true;

        if (typeof window === "undefined") return;
        window.dataLayer = window.dataLayer || [];

        const ecommerceItems = buildEcommerceItems();
        if (ecommerceItems.length === 0) return;

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: "ecom_event",
          event_action: "view_promotion",
          ecommerce: {
            items: ecommerceItems,
          },
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -50px 0px" }
    );

    const el = containerRef.current;
    if (el) observer.observe(el);

    return () => {
      if (el) observer.unobserve(el);
    };
  }, [items, creativeSlotPrefix, getItemPayload]);

  return <div ref={containerRef}>{children}</div>;
}
